import * as _ from 'underscore';

import { ButtonType, OutlineTextFieldWidget } from '@tableau/widgets';
import * as React from 'react';
import { Formatters, Messages } from '../../codegen/Localize';
import { AngularDirective } from '../../common/react/components/AngularDirective';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { ISiteRoleChanges } from '../../ts/controllers/SiteRolesPickerCtrl';
import { IAuthenticationProviderSelectorModel } from '../../ts/directives/tbAuthenticationProviderSelector';
import { AngularContext } from '../../ts/factories/AngularContextFactory';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { EmailValidationHelper } from '../../ts/helpers/EmailValidationHelper';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../../ts/resources/SiteRoleOptions';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { AddLocalUserDialogType } from '../../ts/services/AddLocalUserAction';
import { AddUsersOptionsAction } from '../../ts/services/AddUsersOptionsAction';
import { PasswordPolicyScope, PasswordPolicyService } from '../../ts/services/PasswordPolicyService';
import { ServerService } from '../../ts/services/ServerService';
import { AuthenticationProviderType } from '../../ts/services/SiteAuthenticationSettingsService';
import { ServiceLocator } from '../ServiceLocator';
import { CreateLocalUserOnOtherSiteForm } from './CreateLocalUserOnOtherSiteForm';
import { AddLocalUserToSiteHelperService } from './helpers/AddLocalUserToSiteHelperService';
import { CreateLocalServerUserHelperService } from './helpers/CreateLocalServerUserHelperService';
import { CreateLocalSiteUserHelperService } from './helpers/CreateLocalSiteUserHelperService';
import { ISetupLocalUserFormModelChanges, SetupLocalUserModelHelper } from './helpers/SetupLocalUserModelHelper';
import { NewLocalUserForm } from './NewLocalUserForm';

export const USERNAME_REGEXP: RegExp = new RegExp('[;,]');

const AddLocalUserFormState = {
  Initial: 'initial',
  NewUser: 'newUser',
  OtherSite: 'otherSite',
  NotAvailable: 'notAvailable'
};

export interface AddLocalUsersDialogState {
  readonly busy: boolean;
  readonly userInput: string;
  readonly formModel: IFormModel;
  readonly siteRole: string;
  readonly siteRoleDropdownDisabled: boolean;
  readonly sitePickerMenuDisabled: boolean;
}

export interface AddLocalUsersDialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
  readonly angularContext: AngularContext;
  readonly addLocalUserDialogType: string;
  readonly availableQuota?: number;
  readonly emitSiteRolesChanged: () => void;
  readonly emitUserAddedEvent: () => void;
}

// Model for ux Flow #1:
// => Username is available
export interface INewUserFlowModel {
  displayName: string;
  password: string;
  confirmPassword: string;
  email: string;
  usernameInfo: string;
}

// Model for ux Flow #2:
// => Username exists on another site
export interface IOtherSiteFlowModel {
  displayName: string;
  warningText: string;
  proceedClicked: boolean;
}

// Model for ux Flow #3:
// => Username exists on current site
export interface INotAvailableFlowModel {
  displayName: string;
  errorText: string;
}

// Contains the model for the whole form, including all different flows.
export interface IFormModel {
  username: string;
  formState: string;
  errorText: string;
  newUserFlowModel: INewUserFlowModel;
  otherSiteFlowModel: IOtherSiteFlowModel;
  notAvailableFlowModel: INotAvailableFlowModel;
  authenticationProviderSelectorModel: IAuthenticationProviderSelectorModel;
}

export interface AuthenticationProviderSelectorModelScope extends ng.IScope {
  authenticationProviderSelectorModel?: IAuthenticationProviderSelectorModel;
}

export class AddLocalUsersDialog extends React.Component<
AddLocalUsersDialogProps,
AddLocalUsersDialogState
> {
  private server: ServerService = ServiceLocator.get<ServerService>('ServerService');
  private siteRoleOptionsResource: SiteRoleOptionsResource = ServiceLocator.get<SiteRoleOptionsResource>('SiteRoleOptionsResource');
  public siteRoleChanges: ISiteRoleChanges;
  private scope: AuthenticationProviderSelectorModelScope;
  public setupLocalUserModelHelper = new SetupLocalUserModelHelper();
  public siteRolesOptionResource: SiteRoleOptionsResource = ServiceLocator.get<SiteRoleOptionsResource>('SiteRoleOptionsResource');
  private passwordPolicyService: PasswordPolicyService = ServiceLocator.get<PasswordPolicyService>('PasswordPolicyService');

  public createLocalSiteUserHelperService: CreateLocalSiteUserHelperService = new CreateLocalSiteUserHelperService();
  public createLocalServerUserHelperService: CreateLocalServerUserHelperService =
    new CreateLocalServerUserHelperService();
  public addLocalUserToSiteHelperService: AddLocalUserToSiteHelperService =
    new AddLocalUserToSiteHelperService();
  public passwordPolicyScope: PasswordPolicyScope = this.props.angularContext.scope as PasswordPolicyScope;
  public passwordPoliciesValidationCheck =
    this.passwordPolicyService.loadPoliciesIntoScope(this.passwordPolicyScope, () => this.state.formModel.newUserFlowModel.password, () => {});

  public constructor(props: AddLocalUsersDialogProps) {
    super(props);
    let addUsersOptionsAction: AddUsersOptionsAction = ServiceLocator.get<AddUsersOptionsAction>('AddUsersOptionsAction');
    this.state = {
      busy: false,
      userInput: '',
      siteRoleDropdownDisabled: false,
      formModel: {
        username: '',
        formState: AddLocalUserFormState.Initial,
        errorText: '',
        newUserFlowModel: {
          displayName: '',
          password: '',
          confirmPassword: '',
          email: '',
          usernameInfo: ''
        },
        otherSiteFlowModel: {
          displayName: '',
          warningText: '',
          proceedClicked: false
        },
        notAvailableFlowModel: {
          displayName: '',
          errorText: ''
        },
        authenticationProviderSelectorModel: addUsersOptionsAction.getInitialAuthenticationProviderSelectorModel()
      },
      siteRole: props.availableQuota <= 0 ? SiteRoles.Unlicensed: DefaultSiteRole,
      sitePickerMenuDisabled: false
    };
    this.siteRoleChanges = {
      siteRoles: {},
      serverAdmin: undefined
    };
  }

  public readonly importUsersCallback = () => {
    this.confirm();
  }

  public readonly userNameChangeCallback = (userInput: string) => {
    this.setState({ ...this.state, userInput }, () => this.setupLocalUserFormModel());
  }

  public readonly displayNameChangeCallback = (displayName: string) => {
    let newFormModel = this.state.formModel;
    newFormModel.newUserFlowModel.displayName = displayName;
    this.setState({ formModel: newFormModel });
  }

  public readonly passwordChangeCallback = (password: string) => {
    let newFormModel = this.state.formModel;
    newFormModel.newUserFlowModel.password = password;
    this.setState({ formModel: newFormModel });
  }

  public readonly emailChangeCallback = (email: string) => {
    let newFormModel = this.state.formModel;
    newFormModel.newUserFlowModel.email = email;
    this.setState({ formModel: newFormModel });
  }

  public readonly confirmPasswordChangeCallback = (confirmPassword: string) => {
    let newFormModel = this.state.formModel;
    newFormModel.newUserFlowModel.confirmPassword = confirmPassword;
    this.setState({ formModel: newFormModel });
  }

  public readonly siteRoleChangesCallback = (siteRoleChanges: ISiteRoleChanges) => {
    this.siteRoleChanges = siteRoleChanges;
  }


  public render() {
    return (
      <div className="tb-add-local-user">
        <div className="tb-dialog-title">{Messages.addLocalUser()}</div>
        <div>
        {this.renderAuthenticationProviderSelector()}
          <table className="tb-form-table">
            <tbody>
              <tr>
                <td className="tb-text-input-label-cell">
                  <label> {Messages.username_colon()} </label>
                </td>
                <td className="tb-settings-control tb-input-wide">
                  <OutlineTextFieldWidget
                    handleChange = {this.userNameChangeCallback}
                    testId = "username"
                    disabled={this.state.busy}
                    text={this.state.userInput}
                  />
                  {this.availableQuotaMessage()}
                </td>
              </tr>
              </tbody>
              {this.setupLocalUserForm()}
          </table>
          </div>
          {this.createErrorMessages()}
          <div className="tb-dialog-actions">
            <ButtonWithSpinner
              buttonType={ButtonType.Outline}
              clicked={this.props.dismiss}
              testId={'confirm-action-dialog-cancel'}
              title={Messages.cancel()}
              disabled={this.state.busy}
            />
            <span className="tb-lr-padded">
              <ButtonWithSpinner
                buttonType={ButtonType.Go}
                showSpinner={this.state.busy}
                clicked={this.importUsersCallback}
                testId={'confirm-action-dialog-submit'}
                title={this.submitButtonLabel()}
                disabled={this.isConfirmDisabled()}
              />
            </span>
          </div>
        </div>
    );
  }

  public setupLocalUserFormModel = () => {
    this.setupLocalUserModelHelper.setupLocalUserFormModel(this.state.formModel, this.state.userInput, this.props.addLocalUserDialogType, this.state.sitePickerMenuDisabled)
    .then((setupResult: ISetupLocalUserFormModelChanges) => {
      this.setState({formModel: setupResult.formModel, sitePickerMenuDisabled: setupResult.sitePickerMenuDisabled});
    });
  }

  private renderAuthenticationProviderSelector() {
    if (this.props.addLocalUserDialogType === AddLocalUserDialogType.site && this.server.isSiteSamlEnabled()) {
      return (
          <AngularDirective angularContext={this.props.angularContext}
                            setupScope={this.setupScopeCallback}
                            template={`<tb:authentication-provider-selector model="authenticationProviderSelectorModel"></tb:authentication-provider-selector>`}/>
      );
    }
    return (null);
  }

  private readonly setupScopeCallback = (scope: ng.IScope): ng.IScope => {
    return this.setupScope(scope);
  }

  public setupScope(scope: ng.IScope): ng.IScope {
    this.scope = scope.$new() as AuthenticationProviderSelectorModelScope;
    this.scope.authenticationProviderSelectorModel = this.state.formModel.authenticationProviderSelectorModel;
    this.scope.$watch('authenticationProviderSelectorModel', (newVal: IAuthenticationProviderSelectorModel, oldVal: IAuthenticationProviderSelectorModel) => {
      let formModel = this.state.formModel;
      formModel.authenticationProviderSelectorModel = newVal;
      this.setState({formModel: formModel}, () => {
        if(this.state.userInput.length > 0) {
          this.setupLocalUserFormModel();
        }
      });
    }, true);

    return this.scope;
  }

  private setupLocalUserForm(): JSX.Element {
    if (this.state.formModel.formState === AddLocalUserFormState.NewUser) {
      return (
        <NewLocalUserForm angularContext={this.props.angularContext}
                        availableQuota={this.props.availableQuota}
                        busy={this.state.busy}
                        formModel={this.state.formModel}
                        displayNameChangeHandler={this.displayNameChangeCallback}
                        emailChangeHandler={this.emailChangeCallback}
                        passwordChangeHandler={this.passwordChangeCallback}
                        confirmPasswordChangeHandler={this.confirmPasswordChangeCallback}
                        siteRoleChangesHandler={this.siteRoleChangesCallback}
                        getSiteRoleOptionBySiteRoleHandler={this.getSiteRoleOptionBySiteRoleCallback}
                        onOptionSelectedHandler={this.onOptionSelectedCallback}
                        addLocalUserDialogType={this.props.addLocalUserDialogType}
                        siteRole={this.state.siteRole}
                        sitePickerMenuDisabled={this.state.sitePickerMenuDisabled} />
      );
    } else if (this.state.formModel.formState === AddLocalUserFormState.OtherSite) {
      return (
        <CreateLocalUserOnOtherSiteForm angularContext={this.props.angularContext}
                          availableQuota={this.props.availableQuota}
                          busy={this.state.busy}
                          formModel={this.state.formModel}
                          onProceedClickedHandler={this.onProceedClicked}
                          getSiteRoleOptionBySiteRoleHandler={this.getSiteRoleOptionBySiteRoleCallback}
                          onOptionSelectedHandler={this.onOptionSelectedCallback}
                          addLocalUserDialogType={this.props.addLocalUserDialogType}
                          siteRole={this.state.siteRole}
                          sitePickerMenuDisabled={this.state.sitePickerMenuDisabled} />
      );
    } else if (this.state.formModel.formState === AddLocalUserFormState.NotAvailable) {
      return this.createNotAvailableError();
    }
    return (null);
  }

  private createNotAvailableError(): JSX.Element {
    return (<tbody className="tb-notAvailableFlow-table">
              <tr>
                <td className="tb-text-input-label-cell">
                  <label>{Messages.displayName_colon()}</label>
                </td>
                <td className="tb-immutable-text-cell">
                  <label>{this.state.formModel.notAvailableFlowModel.displayName}</label>
                </td>
              </tr>
              <tr>
                <td/>
                <td className="tb-error-cell">
                  <div className="tb-error-text">{this.state.formModel.notAvailableFlowModel.errorText}</div>
                  <div className="tb-error-text">{Messages.tryAnotherUsername()}</div>
                </td>
              </tr>
            </tbody>);
  }

  private readonly onProceedClicked = () => {
    let formModel = this.state.formModel;
    formModel.otherSiteFlowModel.proceedClicked = true;
    this.setState({sitePickerMenuDisabled: false, formModel});
  }

  private readonly getSiteRoleOptionBySiteRoleCallback = (siteRole: string): SiteRoleOption => {
    return this.siteRoleOptionsResource.getSiteRoleOptionBySiteRole(siteRole);
  }

  private readonly onOptionSelectedCallback = (siteRoleOption: SiteRoleOption): void => {
    this.setState({siteRole: siteRoleOption.role});
  }

  private createErrorMessages(){
    return(<div>
            <div className="tb-error-text">{this.state.formModel.errorText}</div>
            {this.getUsernameError()}
            {this.getPasswordMatchError()}
            {this.getPasswordPoliciesValidationError()}
          </div>);
  }

  private getPasswordPoliciesValidationError(): JSX.Element{
    let passwordPolicyValidation = this.passwordPolicyScope.passwordPolicyValidation;
    if (FeatureFlags.passwordPoliciesEnabled && passwordPolicyValidation.shouldShowErrorMessages()) {
      return (<span data-tb-test-id="passwordPolicyMessages">
                {!passwordPolicyValidation.mustContainLetters() &&
                <div className="tb-error-text" data-tb-test-id="passwordPolicyLetters">
                  {Messages.passwordPolicies_mustContainLetters()}
                </div>}
                {!passwordPolicyValidation.mustContainUppercaseLetters() &&
                <div className="tb-error-text" data-tb-test-id="passwordPolicyUppercaseLetters">
                  {Messages.passwordPolicies_mustContainUppercaseLetters()}
                </div>}
                {!passwordPolicyValidation.mustContainNumbers() &&
                <div className="tb-error-text" data-tb-test-id="passwordPolicyNumbers">
                  {Messages.passwordPolicies_mustContainNumbers()}
                </div>}
                {!passwordPolicyValidation.mustContainSymbols() &&
                <div className="tb-error-text" data-tb-test-id="passwordPolicySymbols">
                  {Messages.passwordPolicies_mustContainSymbols()}
                </div>}
                {!passwordPolicyValidation.mustHaveMinimumPasswordLength() &&
                <div className="tb-error-text" data-tb-test-id="passwordPolicyMinPassword">
                  {Messages.passwordPolicies_minimumLength({MIN_LENGTH: passwordPolicyValidation.getMinLength().toString()})}
                </div>}
                {!passwordPolicyValidation.mustHaveMaximumPasswordLength() &&
                <div className="tb-error-text" data-tb-test-id="passwordPolicyMaxPassword">
                  {Messages.passwordPolicies_maximumLength({MAX_LENGTH: passwordPolicyValidation.getMaxLength().toString()})}
                </div>}
              </span>);
    }
    return (null);
  }

  private getUsernameError(): JSX.Element{
    if (this.hasInvalidUsername()) {
      return(<div className="tb-error-text usernameError">
              {Messages.usernameInvalid()}
              <div> ; , </div>
            </div>);
    }
    return (null);
  }

  public hasInvalidUsername(): boolean {
    return this.state.userInput.length > 0 && USERNAME_REGEXP.test(this.state.userInput);
  }

  private getPasswordMatchError(): JSX.Element{
    if (this.hasNonMatchingPasswords()) {
      return (<div className="tb-error-text passwordMatchingError">{Messages.passwordsMustMatch()}</div>);
    }
    return (null);
  }

  public hasNonMatchingPasswords(): boolean {
    let password = this.state.formModel.newUserFlowModel.password;
    let confirmPassword = this.state.formModel.newUserFlowModel.confirmPassword;
    if (password.length > 0 && confirmPassword.length > 0) {
      if (password !== confirmPassword) {
        return true;
      }
    }
    return false;
  }

  public submitButtonLabel() {
    if(!this.server.isTableauIdSupported() && this.state.formModel.otherSiteFlowModel.proceedClicked) {
      return Messages.addUserToSite();
    }
    return Messages.addUserDialogSubmit();
   }

  private availableQuotaMessage(): JSX.Element {
    if (_.isNumber(this.props.availableQuota)) {
      return (
        <div className={'tb-available-quota tb-row'}>
          {Messages.availableQuota({NUM_USERS: this.props.availableQuota, NUM_USERS_LOC: Formatters.formatCount(this.props.availableQuota)})}
        </div>
      );
    }
    return (null);
  }

  private shouldCheckPasswordFields(): boolean {
    return this.state.formModel.authenticationProviderSelectorModel.authenticationProvider.type === AuthenticationProviderType.default && !this.server.isAuthenticationSaml();
  }

  public isLocalUserInputsValid(): boolean {
    if (this.state.formModel.username.length > 0 &&
        this.state.formModel.newUserFlowModel.displayName.length > 0 &&
        !this.hasInvalidUsername() &&
        !this.hasInvalidEmail()) {
        if (this.shouldCheckPasswordFields()) {
          const hasPassword = this.state.formModel.newUserFlowModel.password.length > 0;
          const hasConfirmedPassword = this.state.formModel.newUserFlowModel.confirmPassword.length > 0;
          const passwordIsValid = this.passwordPoliciesValidationCheck();

          return hasPassword && hasConfirmedPassword && !this.hasNonMatchingPasswords() && passwordIsValid;
        }
        return true;
    }
    return false;
  }

  public hasInvalidEmail() {
    if (this.state.formModel.newUserFlowModel.email) {
      return !EmailValidationHelper.isValidEmail(this.state.formModel.newUserFlowModel.email);
    }
    return false;
  }

  public isConfirmDisabled(): boolean {
    if (this.state.busy) {
      return true;
    }

    switch (this.state.formModel.formState) {

      case AddLocalUserFormState.Initial:
      case AddLocalUserFormState.NewUser:
        return !this.isLocalUserInputsValid();

      case AddLocalUserFormState.OtherSite:
        return !this.state.formModel.otherSiteFlowModel.proceedClicked;

      case AddLocalUserFormState.NotAvailable:
        return true;
    }

    return false;
  }

  private setErrorTextCallback = (errorMsg: string)  => {
    let formModel = this.state.formModel;
    formModel.errorText = errorMsg;
    this.setState({formModel});
  }

  private setBusyState = (busy: boolean) => {
    this.setState({busy});
  }

  public confirm() {
    this.setState({busy: true});
    const username = this.state.formModel.username;
    const authenticationProvider = this.state.formModel.authenticationProviderSelectorModel.authenticationProvider;
    switch (this.state.formModel.formState) {

      case AddLocalUserFormState.NewUser:
        if (this.props.addLocalUserDialogType === AddLocalUserDialogType.site) {
          this.createLocalSiteUserHelperService.createLocalSiteUser(username, this.state.formModel.newUserFlowModel, this.state.siteRole, authenticationProvider, this.props, this.setErrorTextCallback, this.setBusyState);
        } else if (this.props.addLocalUserDialogType === AddLocalUserDialogType.server) {
          this.createLocalServerUserHelperService.createLocalServerUser(username, this.state.formModel.newUserFlowModel, authenticationProvider, this.siteRoleChanges, this.props, this.setBusyState);
        }
        break;

      case AddLocalUserFormState.OtherSite:
        this.addLocalUserToSiteHelperService.addUserToSite(username, this.state.siteRole, this.props, this.setErrorTextCallback, this.setBusyState);
        break;
    }
  }
}
